/**
 * midiAnalysis.ts
 *
 * Chord regions and key areas for the MIDI file player, from the backend's
 * POST /sentiserver/midi (sentiserver.py analyze_midi_file; the contract is in
 * the backend's key_tracker_attempt/HANDOFF.md, "FOR THE FRONTEND SESSION").
 * The file is analysed once when it loads, and the result is cached by the
 * file's SHA-256 — the same hash a chart entry's midiSource.fileKey carries
 * (CHORD-TIMESTAMP-SPEC.md).
 *
 * REGIONS AND PINS
 * ----------------
 * Regions are contiguous, with no gaps even over silence: each ends where the
 * next starts, the last at the file's end. A pin is the start of a region, so
 * every pin after the first is the boundary two regions share.
 *
 * A region's notes follow the backend's naming rule (HANDOFF.md, "A region's
 * notes"), and every edit here re-derives them by that rule from the
 * backend's `sounding` stretches, so an untouched region always gets the
 * backend's own notes and name back.
  */

import axios from 'axios';
import { ChordSegment } from './chordChartExport';
import { postWithRetry } from './chordCandidates';
import { ChartEntryRef, KeyCenter, KeyCenterRegion, keyCentersEqual } from './keyCenterRegions';
import { getModeFromPcSet } from '../utils';

/**
 * A region as the backend sends it (its legacy `sonorities` field is unused).
 * `index` is the backend's and is kept through edits: it identifies the region
 * and is what key areas' `region_index` refers to. `segment_feel` /
 * `segment_read` are null when there is nothing to name (a single pitch class,
 * as /sentiserver gives none), and undefined for a new region not yet named.
 *
 * When an edit changes a region's notes it keeps its old name on show until
 * the new one arrives, rather than a blank: `feel_for` / `read_for` record
 * which notes (voicingKey) each setting's name was given for. Unset means the
 * name is for the region's own notes, as every backend region's is.
 */
export type ChordRegion = {
  index: number;
  start: number;
  end: number;
  /** When the region's chord is first heard (>= start): its first moment
   * that reads as its chord. */
  heard: number;
  voicing: number[];
  is_scale: boolean;
  segment_feel?: ChordSegment | null;
  segment_read?: ChordSegment | null;
  scale_name?: string;
  feel_for?: string;
  read_for?: string;
  /** False: melody, not a chord. Drawn faint, left out of promotion and of
   * Follow's highlighting; its chart chord, if it has one, stays. Unset is
   * enabled. The user's, never the backend's. */
  enabled?: boolean;
  /** The user's corrections to the transcription, never to the file: notes
   * added across the whole region, and file notes struck out of it. Neither
   * changes playback or scrubbing, only what the region sounds as, is named
   * and charted as (regionVoicing). `voicing` stays the file's own notes. */
  addedNotes?: number[];
  removedNotes?: number[];
};

/** Whether a region counts as a chord (see `enabled`). */
export const isEnabled = (region: ChordRegion) => region.enabled !== false;

/**
 * A region's notes as the user has corrected them: the file's, less any
 * struck out, plus any added. A note struck out that no longer sounds in the
 * region (its span moved) simply has nothing to remove.
 */
export function regionVoicing(region: ChordRegion): number[] {
  const removed = region.removedNotes || [];
  const added = region.addedNotes || [];
  if (removed.length === 0 && added.length === 0) return region.voicing;
  return Array.from(new Set([...region.voicing.filter(n => !removed.includes(n)), ...added])).sort((a, b) => a - b);
}

/** The corrections that make a region's notes `notes`: what to add, what to strike out. */
export function amendmentsFor(region: ChordRegion, notes: number[]): { addedNotes: number[]; removedNotes: number[] } {
  const wanted = Array.from(new Set(notes));
  return {
    addedNotes: wanted.filter(n => !region.voicing.includes(n)).sort((a, b) => a - b),
    removedNotes: region.voicing.filter(n => !wanted.includes(n)),
  };
}

export type Sounding = [number, number, number];

export type KeyArea = {
  start: number;
  end: number;
  region_index: number;
  key: KeyCenter;
  confidence: number;
};

export type MidiAnalysis = {
  analysis_version: number;
  subset_window: number;
  duration: number;
  /** "timeline": regions are named by regionNotes' rule. */
  region_rule: string;
  regions: ChordRegion[];
  /** [start, end, pitch]: each stretch a pitch sounds, pedal included, in
   * order of start. The notes every region rule reads. */
  sounding: Sounding[];
  key_areas: KeyArea[];
  global_key: KeyCenter | null;
  /** Non-null only when the file cycles fast through distant keys (Giant
   * Steps). Offered to the user, never switched to automatically. */
  fast_key_areas: KeyArea[] | null;
};

/** The backend's default subset window (analysis version 8 and later). */
export const DEFAULT_SUBSET_WINDOW = 0.5;

/** A 90-minute concert takes ~15 s to analyse; leave room past that. */
const MIDI_REQUEST_TIMEOUT_MS = 120000;

/** SHA-256 of the file's bytes, hex: a chart entry's midiSource.fileKey. */
export async function fileKeyOf(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, '0')).join('');
}

const analysisCache = new Map<string, Promise<MidiAnalysis>>();

/**
 * The analysis of a file, from the cache when this file was analysed before
 * with the same subset window. A failed request is dropped from the cache so
 * loading the file again tries again.
 */
export function fetchMidiAnalysis(
  server: string,
  bytes: ArrayBuffer,
  fileKey: string,
  subsetWindow: number = DEFAULT_SUBSET_WINDOW,
): Promise<MidiAnalysis> {
  const cacheKey = `${fileKey}:${subsetWindow}`;
  const cached = analysisCache.get(cacheKey);
  if (cached) return cached;
  const request = axios.post(`${server}/midi`, bytes, {
    headers: { 'Content-Type': 'application/octet-stream' },
    params: subsetWindow === DEFAULT_SUBSET_WINDOW ? undefined : { subset_window: subsetWindow },
    timeout: MIDI_REQUEST_TIMEOUT_MS,
  }).then(response => response.data as MidiAnalysis);
  analysisCache.set(cacheKey, request);
  request.catch(() => analysisCache.delete(cacheKey));
  return request;
}

/** What to tell the user when the analysis fails: the server's own words when it sent any. */
export function analysisErrorMessage(err: any): string {
  const response = err && err.response;
  if (response && response.data && response.data.error) return response.data.error;
  if (response && response.status === 429) return 'too many files analysed this minute; try again shortly';
  return 'the analysis server did not answer';
}

/** The segment the user's READ / FEEL setting shows. */
export function shownSegment(region: ChordRegion, notationLegible: boolean): ChordSegment | null | undefined {
  return notationLegible ? region.segment_read : region.segment_feel;
}

// ---------------------------------------------------------------------------
// The naming rule, and edits
// ---------------------------------------------------------------------------

/** A carry-over that stops this soon after the chord is heard is not part of it. */
const FRONT_TOLERANCE = 0.075;
/** locate_chords' combine window: a note struck this soon before the end,
 * still sounding at it, is the next chord arriving early. */
const BACK_TOLERANCE = 0.15;
const EPS = 1e-9;

/**
 * Whether a stretch of sound [s, e) belongs to the region [start, end) whose
 * chord is first heard at `heard`: it sounds inside the region, and is
 * neither
 * - at the front, a carry-over from before `start` that stops within 75 ms
 *   of the chord being heard (Waltz 3.96: the Dm7's F stops 55 ms after the
 *   Gm7 is heard, so the region is Gm7, not Bb/G; Waltz 1:46.98: the low G#
 *   held under the re-struck upper notes stops 92 ms after, so it stays and
 *   the region is E13/G#), nor
 * - at the back, a note struck after the chord is heard, within the combine
 *   window before `end`, that still sounds at `end`: the next chord arriving
 *   early. One struck there that stops before `end` stays in.
 * The backend's timeline.region_notes, tolerances included.
 */
export function countsInRegion(s: number, e: number, start: number, end: number, heard: number): boolean {
  if (s >= end - EPS) return false;
  if (e <= start + EPS) return false;
  const h = Math.max(start, heard);
  if (s < start - EPS && e <= h + FRONT_TOLERANCE + EPS) return false;
  return !(s > h + EPS && s >= end - BACK_TOLERANCE - EPS && e > end + EPS);
}

/** A region's notes, by countsInRegion over the backend's `sounding`. */
export function regionNotes(sounding: Sounding[], start: number, end: number, heard: number): number[] {
  const pitches = new Set<number>();
  for (const [s, e, p] of sounding) {
    if (s >= end - EPS) break; // in order of start
    if (countsInRegion(s, e, start, end, heard)) pitches.add(p);
  }
  return Array.from(pitches).sort((a, b) => a - b);
}

export const voicingKey = (voicing: number[]) => voicing.join(',');

/**
 * A region over a new span, its notes re-derived. It keeps its own `heard`
 * unless its start has passed it.
 */
function respanned(region: ChordRegion, start: number, end: number, sounding: Sounding[]): ChordRegion {
  if (start === region.start && end === region.end) return region;
  return rederived(region, start, end, Math.max(region.heard, start), sounding);
}

/**
 * `region` over [start, end) heard at `heard`, with the notes the rule gives
 * that span. Its names stay if its notes did. Otherwise each setting is named
 * at once from any name already known for the new notes, and where none is
 * keeps showing its old name, marked as given for the old notes, until named.
 */
function rederived(region: ChordRegion, start: number, end: number, heard: number, sounding: Sounding[]): ChordRegion {
  const voicing = regionNotes(sounding, start, end, heard);
  if (voicingKey(voicing) === voicingKey(region.voicing)) return { ...region, start, end, heard };
  const old = voicingKey(regionVoicing(region));
  let next: ChordRegion = {
    ...region, start, end, heard, voicing,
    feel_for: region.feel_for !== undefined ? region.feel_for : old,
    read_for: region.read_for !== undefined ? region.read_for : old,
  };
  return withKnownNames(next);
}

/** A region given at once any name already known for its notes, for each setting. */
export function withKnownNames(region: ChordRegion): ChordRegion {
  let next = region;
  const notes = regionVoicing(region);
  [false, true].forEach(notationLegible => {
    if (!needsName(next, notationLegible)) return;
    const known = cachedName(notes, notationLegible);
    if (known) next = withName(next, known, notationLegible);
  });
  return next;
}

/**
 * A region with corrected notes (amendmentsFor): it keeps showing its old
 * name, marked as given for its old notes, until named for the new ones,
 * unless a name for them is already known.
 */
export function withAmendments(region: ChordRegion, amendments: { addedNotes: number[]; removedNotes: number[] }): ChordRegion {
  const old = voicingKey(regionVoicing(region));
  const next: ChordRegion = {
    ...region,
    addedNotes: amendments.addedNotes.length > 0 ? amendments.addedNotes : undefined,
    removedNotes: amendments.removedNotes.length > 0 ? amendments.removedNotes : undefined,
    feel_for: region.feel_for !== undefined ? region.feel_for : old,
    read_for: region.read_for !== undefined ? region.read_for : old,
  };
  if (voicingKey(regionVoicing(next)) === old) return { ...next, feel_for: region.feel_for, read_for: region.read_for };
  return withKnownNames(next);
}

/**
 * Moves the boundary at the start of region `k` to `time`, from the regions
 * as they were when the drag began, so dragging back restores them. The
 * boundary pushes the pins beyond it rather than squeeze a region below
 * `minWidth`, and stops where every region on that side is at minWidth. The
 * first pin moves too, down to the start of the file: the stretch before it
 * belongs to no region (the backend starts the first region at the first
 * note, not at 0:00).
 */
export function moveBoundary(original: ChordRegion[], k: number, time: number, minWidth: number, sounding: Sounding[]): ChordRegion[] {
  const n = original.length;
  if (k < 0 || k >= n) return original;
  const fileEnd = original[n - 1].end;
  const pins = original.map(r => r.start);
  pins[k] = Math.max(k * minWidth, Math.min(fileEnd - (n - k) * minWidth, time));
  for (let i = k + 1; i < n && pins[i] < pins[i - 1] + minWidth; i++) pins[i] = pins[i - 1] + minWidth;
  for (let i = k - 1; i >= 0 && pins[i] > pins[i + 1] - minWidth; i--) pins[i] = pins[i + 1] - minWidth;
  const next = original.map((r, i) => respanned(r, pins[i], i + 1 < n ? pins[i + 1] : fileEnd, sounding));
  return next.every((r, i) => r === original[i]) ? original : next;
}

/**
 * A new pin at `time`, splitting the region it falls in. The new region runs
 * from `time` to the old region's end and is heard from its own start, as the
 * backend's contract has a region the user creates; the old region keeps its
 * start and `heard` and ends at `time`. Both take the notes the rule gives
 * their spans. `index` must be unused by any region. No split before the
 * first region, or within `minWidth` of a pin or the file's end.
 */
export function splitRegion(regions: ChordRegion[], time: number, index: number, minWidth: number, sounding: Sounding[]): ChordRegion[] {
  const k = regionAt(regions, time);
  if (k < 0) return regions;
  const region = regions[k];
  if (time - region.start < minWidth || region.end - time < minWidth) return regions;
  // Shows the split region's name until its own arrives
  const created = rederived({ ...region, index }, time, region.end, time, sounding);
  const next = regions.slice();
  next.splice(k, 1, respanned(region, region.start, time, sounding), created);
  return next;
}

/**
 * Merges regions `kFirst` to `kLast` into one: the first keeps its `index`
 * and `heard`, and its span runs to the last one's end, with the notes the
 * rule gives it. Keeping the first is what keeps a chart chord linked to it
 * linked (its start does not move); the boundaries inside are what go.
 */
export function mergeRegions(regions: ChordRegion[], kFirst: number, kLast: number, sounding: Sounding[]): ChordRegion[] {
  if (kFirst < 0 || kLast >= regions.length || kLast <= kFirst) return regions;
  const span = regions.slice(kFirst, kLast + 1);
  const union = (lists: (number[] | undefined)[]) => {
    const all = Array.from(new Set(lists.reduce<number[]>((acc, list) => acc.concat(list || []), []))).sort((a, b) => a - b);
    return all.length > 0 ? all : undefined;
  };
  // The corrections of every region merged go with it
  const first = { ...regions[kFirst], addedNotes: union(span.map(r => r.addedNotes)), removedNotes: union(span.map(r => r.removedNotes)) };
  const merged = respanned(first, first.start, regions[kLast].end, sounding);
  const next = regions.slice();
  next.splice(kFirst, kLast - kFirst + 1, merged);
  return next;
}

/**
 * Where the regions with these indices lie in `regions`, as [kFirst, kLast],
 * when there are at least two of them and they are side by side; otherwise
 * null (nothing to merge).
 */
export function adjacentRange(regions: ChordRegion[], indices: number[]): [number, number] | null {
  const ks = regions.map((r, k) => (indices.includes(r.index) ? k : -1)).filter(k => k >= 0);
  if (ks.length < 2) return null;
  return ks[ks.length - 1] - ks[0] === ks.length - 1 ? [ks[0], ks[ks.length - 1]] : null;
}

/**
 * The selection a click on region `index` makes, in the order the regions lie:
 * - plain: that region alone;
 * - shift: every region from the anchor (the last plain or toggle click) to
 *   this one, inclusive; with no anchor, as plain;
 * - toggle (⌘ / Ctrl): this one added or taken out, the rest kept.
 */
export function selectionAfterClick(
  regions: ChordRegion[], selected: number[], anchor: number | null, index: number,
  modifiers: { shift: boolean; toggle: boolean },
): number[] {
  let chosen: number[] = [index];
  if (modifiers.shift && anchor !== null) {
    const a = regions.findIndex(r => r.index === anchor);
    const b = regions.findIndex(r => r.index === index);
    if (a >= 0 && b >= 0) chosen = regions.slice(Math.min(a, b), Math.max(a, b) + 1).map(r => r.index);
  } else if (modifiers.toggle) {
    chosen = selected.includes(index) ? selected.filter(i => i !== index) : [...selected, index];
  }
  return regions.filter(r => chosen.includes(r.index)).map(r => r.index);
}

// ---------------------------------------------------------------------------
// Key areas
// ---------------------------------------------------------------------------

/**
 * The file's key areas are kept as the chart keeps its key centers
 * (utils/keyCenterRegions.ts), anchored to region starts instead of chord IDs:
 * a region stands in for a chord under this ID, so every rule there (setting
 * a key, ending one, an anchor sliding when its region is merged away,
 * neighbours with the same key merging) applies to the file unchanged.
 */
export const keyAreaId = (regionIndex: number) => `r${regionIndex}`;

/** The regions as the key center rules see a chart's entries. */
export const regionRefs = (regions: ChordRegion[]): ChartEntryRef[] =>
  regions.map(r => ({ id: keyAreaId(r.index), isSeparator: false }));

/** The analysis's key areas as auto key centers. An area in its global key is
 * the global key, not a local one: a first one is left out, and one after a
 * local key is a "none", so it follows the global key when that is changed.
 * An area in the key already in force is folded into it. */
export function keyAreasFromAnalysis(areas: KeyArea[], globalKey: KeyCenter | null): KeyCenterRegion[] {
  const result: KeyCenterRegion[] = [];
  areas.slice().sort((a, b) => a.start - b.start).forEach(area => {
    const key: KeyCenter = { pitchClass: area.key.pitchClass, mode: area.key.mode === 1 ? 1 : 0 };
    const isGlobal = globalKey !== null && keyCentersEqual(globalKey, key);
    const inForce = result.length > 0 ? result[result.length - 1].keyCenter : null;
    if (isGlobal ? inForce === null : keyCentersEqual(inForce, key)) return;
    result.push({ startChordId: keyAreaId(area.region_index), keyCenter: isGlobal ? null : key, source: 'auto' });
  });
  return result;
}

/**
 * Each region's key in one pass: the key area covering it, the global key
 * where none does (or one ends), absent where there is neither.
 */
export function keysByRegion(regions: ChordRegion[], keyAreas: KeyCenterRegion[], globalKey: KeyCenter | null): Map<number, KeyCenter> {
  const byAnchor = new Map(keyAreas.map(area => [area.startChordId, area]));
  const keys = new Map<number, KeyCenter>();
  let current = globalKey;
  regions.forEach(r => {
    const area = byAnchor.get(keyAreaId(r.index));
    if (area) current = area.keyCenter === null ? globalKey : area.keyCenter;
    if (current !== null) keys.set(r.index, current);
  });
  return keys;
}

/**
 * The stretches of the file in one key, for drawing: where each starts and
 * ends, its key (null: none), the region it starts on, and whether it is the
 * user's own, the analysis's, or the global key showing through before any
 * key is set. `usesGlobal`: started by a "use global key" marker (the user's
 * or the analysis's, by `source`), its key the global key.
 */
export type KeyRun = { start: number; end: number; keyCenter: KeyCenter | null; regionIndex: number; source: 'manual' | 'auto' | 'global'; usesGlobal: boolean };
export function keyRuns(regions: ChordRegion[], keyAreas: KeyCenterRegion[], globalKey: KeyCenter | null): KeyRun[] {
  const byAnchor = new Map(keyAreas.map(area => [area.startChordId, area]));
  const runs: KeyRun[] = [];
  regions.forEach((r, k) => {
    const area = byAnchor.get(keyAreaId(r.index));
    if (k === 0 || area) {
      const keyCenter = area && area.keyCenter !== null ? area.keyCenter : globalKey;
      runs.push({ start: r.start, end: r.end, keyCenter, regionIndex: r.index, source: area ? area.source : 'global', usesGlobal: !!area && area.keyCenter === null });
    } else {
      runs[runs.length - 1].end = r.end;
    }
  });
  return runs;
}

/** The region sounding at `time`, or -1 before the first. */
export function regionAt(regions: ChordRegion[], time: number): number {
  let found = -1;
  for (let i = 0; i < regions.length && regions[i].start <= time; i++) found = i;
  return found;
}

// ---------------------------------------------------------------------------
// Naming regions
// ---------------------------------------------------------------------------

/** One setting's name for a set of notes; undefined while unknown. */
type NameResult = { is_scale: boolean; segment: ChordSegment | null; scale_name?: string };

const nameCache = new Map<string, NameResult>();
const nameKey = (notes: number[], notationLegible: boolean) => `${notationLegible ? 'R' : 'F'}:${notes.join(',')}`;

/** Server-side MAX_BATCH_CHORDS. */
const MAX_BATCH_CHORDS = 128;

/**
 * Seeds the name cache with the backend's names, so a region dragged back to
 * where it started is named at once, and exactly as the backend named it.
 */
export function rememberNames(regions: ChordRegion[]): void {
  regions.forEach(region => {
    // A scale region has a scale name and no segments, for either setting
    const feel = region.is_scale ? null : region.segment_feel;
    const read = region.is_scale ? null : region.segment_read;
    if (feel !== undefined) {
      nameCache.set(nameKey(region.voicing, false), { is_scale: region.is_scale, segment: feel, scale_name: region.scale_name });
    }
    if (read !== undefined) {
      nameCache.set(nameKey(region.voicing, true), { is_scale: region.is_scale, segment: read, scale_name: region.scale_name });
    }
  });
}

/** The cached name of these notes for one setting, or undefined if not yet named. */
export function cachedName(notes: number[], notationLegible: boolean): NameResult | undefined {
  // The backend names nothing with fewer than two notes.
  if (notes.length < 2) return { is_scale: false, segment: null };
  return nameCache.get(nameKey(notes, notationLegible));
}

/**
 * The locked key a region's spelling depends on, with the settings the Chord
 * Symbol panel sends alongside it. Null when no key is locked: a chord is then
 * spelled by its own root and quality and needs nothing from the server.
 */
export type SpellingKey = { keyCenter: number; keyMode: number; preferModeBorrowing: boolean };

// The spelling mode (0 major, 1 minor) of a voicing's tonic scale in a locked
// key: getModeFromPcSet(tonic_scales_notes[0]), as the panel spells
const spellingCache = new Map<string, number>();
const spellingCacheKey = (notesKey: string, notationLegible: boolean, key: SpellingKey) =>
  `${notationLegible ? 'R' : 'F'}:${key.keyCenter}:${key.keyMode}:${key.preferModeBorrowing ? 1 : 0}:${notesKey}`;

/**
 * The mode to spell a region's chord in, under a locked key: that of the
 * tonic scale its notes imply there, so a region is spelled as the Chord
 * Symbol panel spells the same notes (an Fm in C major with mode borrowing
 * preferred spells against C minor). `notesKey` is the voicingKey of the
 * notes the shown name was given for. Undefined until fetched.
 */
export function cachedSpellingMode(notesKey: string, notationLegible: boolean, key: SpellingKey): number | undefined {
  return spellingCache.get(spellingCacheKey(notesKey, notationLegible, key));
}

/** The voicingKey of the notes a region's shown name was given for. */
export function namedFor(region: ChordRegion, notationLegible: boolean): string {
  const namedForKey = notationLegible ? region.read_for : region.feel_for;
  return namedForKey !== undefined ? namedForKey : voicingKey(regionVoicing(region));
}

/**
 * Names every voicing for one READ / FEEL setting, through the cache and then
 * /sentiserver/batch in chunks. The top segment of the setting's own ordering,
 * as the backend took it: cs_options_segments_notation[0] for READ,
 * cs_options_segments[0] for FEEL. With a locked key the same request also
 * gives each voicing's spelling mode (cachedSpellingMode): the key center
 * changes no names, so one request does both. A chord the server could not
 * name is left out of the cache and named null here, so a later pass may try
 * it again.
 */
export async function nameVoicings(server: string, voicings: number[][], notationLegible: boolean, spelling: SpellingKey | null): Promise<NameResult[]> {
  const wanted = voicings.filter(v => v.length >= 2 && (
    cachedName(v, notationLegible) === undefined
    || (spelling !== null && cachedSpellingMode(voicingKey(v), notationLegible, spelling) === undefined)
  ));
  const distinct = Array.from(new Map(wanted.map(v => [voicingKey(v), v])).values());
  for (let i = 0; i < distinct.length; i += MAX_BATCH_CHORDS) {
    const chunk = distinct.slice(i, i + MAX_BATCH_CHORDS);
    const response = await postWithRetry(`${server}/batch`, {
      chords: chunk.map(notes => ({ notes })),
      notationLegible,
      ...(spelling !== null
        ? { keyCenter: spelling.keyCenter, keyMode: spelling.keyMode, preferModeBorrowing: spelling.preferModeBorrowing }
        : { keyCenter: -1 }),
      // No 32-note gravity trim: the backend names a file's regions from all
      // their notes, and a pedalled region must re-name the same
      trim: false,
    });
    const results = (response.data && response.data.results) || [];
    chunk.forEach((notes, j) => {
      const result = results[j];
      if (!result || result.error) return;
      const segments = notationLegible ? result.cs_options_segments_notation : result.cs_options_segments;
      nameCache.set(nameKey(notes, notationLegible), {
        is_scale: !!result.is_scale,
        segment: segments && segments.length ? segments[0] : null,
        scale_name: result.scale_name,
      });
      if (spelling !== null) {
        const tonicScales = result.tonic_scales_notes;
        spellingCache.set(
          spellingCacheKey(voicingKey(notes), notationLegible, spelling),
          getModeFromPcSet(tonicScales && tonicScales.length ? tonicScales[0] : undefined),
        );
      }
    });
  }
  return voicings.map(v => cachedName(v, notationLegible) || { is_scale: false, segment: null });
}

/** Whether a region still has to be named for this setting: its name is
 * for other notes, or it has none. A scale region from the backend has a
 * scale name and no segments. */
export function needsName(region: ChordRegion, notationLegible: boolean): boolean {
  const namedFor = notationLegible ? region.read_for : region.feel_for;
  if (namedFor !== undefined && namedFor !== voicingKey(regionVoicing(region))) return true;
  return !region.is_scale && shownSegment(region, notationLegible) === undefined;
}

/** A region with one setting's name filled in, for the notes it has now. */
export function withName(region: ChordRegion, name: NameResult, notationLegible: boolean): ChordRegion {
  const scale = { is_scale: name.is_scale, scale_name: name.scale_name };
  const forNow = voicingKey(regionVoicing(region));
  return notationLegible
    ? { ...region, ...scale, segment_read: name.segment, read_for: forNow }
    : { ...region, ...scale, segment_feel: name.segment, feel_for: forNow };
}
